import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { serviceJsonLd, filArianeJsonLd, faqJsonLd } from "../utils/donneesStructurees";
import { trackSimulatorClick, trackPhoneClick } from "../utils/analytics";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  lignesBareme,
  formaterEuros,
  plafond,
  PLAFOND_TRAVAUX,
  AIDE_MAX,
  ANNEE_BAREME,
  SOURCE_BAREME,
} from "../utils/plafonds";
import { Phone, ExternalLink, Calculator, AlertTriangle } from "lucide-react";
import GuidesLies from "../components/GuidesLies";

/**
 * « Plafond de ressources MaPrimeAdapt' » est l'une des requêtes les plus
 * fréquentes du sujet, et aucune page du site ne donnait le chiffre. Tous les
 * montants viennent de src/utils/plafonds.js, source unique partagée avec le
 * simulateur.
 */

const FAQ = [
  {
    question: "Quel revenu est pris en compte pour MaPrimeAdapt' ?",
    answer:
      "C'est le revenu fiscal de référence (RFR) qui compte, pas le salaire ni la pension nette. Il figure sur votre dernier avis d'imposition, en première page. L'ANAH retient le RFR de l'année N-1 et additionne ceux de toutes les personnes qui occupent le logement, y compris quand elles font des déclarations séparées.",
  },
  {
    question: "Mon enfant qui m'héberge compte-t-il dans le ménage ?",
    answer:
      "Le nombre de personnes retenu est celui des occupants du logement, pas celui du foyer fiscal. Si un enfant adulte vit sous votre toit, il compte dans l'effectif — et ses revenus sont additionnés aux vôtres. C'est souvent ce qui fait basculer un dossier d'une catégorie à l'autre, dans un sens comme dans l'autre.",
  },
  {
    question: "Je dépasse légèrement le plafond, existe-t-il un recours ?",
    answer:
      "Les plafonds ne se négocient pas. En revanche, deux choses valent la peine d'être vérifiées : le RFR retenu est celui de l'année N-1, or un départ à la retraite fait souvent chuter le revenu d'une année sur l'autre ; et le nombre d'occupants doit être exact. Si vous restez au-dessus, d'autres dispositifs prennent le relais — caisse de retraite, volet aides techniques de l'APA, PCH, aides locales.",
  },
  {
    question: "Les plafonds sont-ils les mêmes partout en France ?",
    answer:
      "Non. L'Île-de-France dispose de son propre barème, plus élevé que celui du reste du pays, pour tenir compte du coût de la vie. Les montants indiqués sur cette page sont les plafonds franciliens : ce sont ceux qui s'appliquent à Paris et dans les cinq autres départements où nous intervenons.",
  },
  {
    question: "Les plafonds changent-ils chaque année ?",
    answer:
      `Oui, ils sont revalorisés au 1er janvier. Le barème publié ici est celui en vigueur en ${ANNEE_BAREME}. Si votre situation était juste au-dessus l'an dernier, elle mérite d'être réexaminée : la revalorisation annuelle fait entrer chaque année de nouveaux ménages dans le dispositif.`,
  },
];

const PlafondsRessources = () => {
  const lignes = lignesBareme();

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre={`Plafonds de ressources MaPrimeAdapt' ${ANNEE_BAREME} en Île-de-France`}
        description={`Barème ${ANNEE_BAREME} des plafonds de revenu fiscal de référence pour MaPrimeAdapt' en Île-de-France, de 1 à 5 personnes, et les taux d'aide correspondants.`}
        chemin="/plafonds-ressources-maprimeadapt"
        donnees={[
          serviceJsonLd({
            nom: "Vérification de l'éligibilité à MaPrimeAdapt'",
            description:
              "Barème des plafonds de ressources de l'ANAH applicables à MaPrimeAdapt' en Île-de-France et accompagnement au montage du dossier.",
            url: "/plafonds-ressources-maprimeadapt",
          }),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "MaPrimeAdapt'", url: "/maprimeadapt" },
            { nom: "Plafonds de ressources", url: "/plafonds-ressources-maprimeadapt" },
          ]),
          faqJsonLd(FAQ),
        ]}
      />

      <Header />

      <main id="contenu">

      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Plafonds de ressources MaPrimeAdapt' {ANNEE_BAREME} en Île-de-France
            </h1>
            <p className="text-lg text-gray-700 leading-relaxed">
              MaPrimeAdapt' est réservée aux ménages dont les ressources ne dépassent pas
              les plafonds de l'ANAH. Deux catégories existent, et elles ne donnent pas
              droit au même taux d'aide. L'Île-de-France dispose de son propre barème, plus
              élevé que celui du reste de la France : ce sont ces montants-là qui
              s'appliquent si vous habitez Paris, la Seine-et-Marne, l'Essonne, les
              Hauts-de-Seine, la Seine-Saint-Denis ou le Val-d'Oise.
            </p>
          </div>
        </div>
      </section>

      {/* Le tableau */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">
              Le barème {ANNEE_BAREME}, par nombre de personnes au foyer
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              Les montants ci-dessous sont des plafonds de <strong>revenu fiscal de
              référence</strong>, cumulé pour l'ensemble des personnes qui occupent le
              logement.
            </p>

            <div
              className="overflow-x-auto rounded-lg border border-gray-200"
              tabIndex="0"
              role="group"
              aria-label="Tableau des plafonds de ressources, défilement horizontal"
            >
              <table className="w-full text-left">
                <caption className="sr-only">
                  Plafonds de revenu fiscal de référence en Île-de-France pour
                  MaPrimeAdapt' en {ANNEE_BAREME}
                </caption>
                <thead className="bg-gray-100">
                  <tr>
                    <th scope="col" className="px-4 py-4 font-semibold text-gray-900">
                      Personnes au foyer
                    </th>
                    <th scope="col" className="px-4 py-4 font-semibold text-blue-700">
                      Très modestes — aide de 70 %
                    </th>
                    <th scope="col" className="px-4 py-4 font-semibold text-yellow-800">
                      Modestes — aide de 50 %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <tr key={l.personnes} className="border-t border-gray-200">
                      <th scope="row" className="px-4 py-4 font-medium text-gray-900">
                        {l.personnes} personne{l.personnes > 1 ? "s" : ""}
                      </th>
                      <td className="px-4 py-4 text-gray-800">
                        jusqu'à {formaterEuros(l.tresModestes)}
                      </td>
                      <td className="px-4 py-4 text-gray-800">
                        jusqu'à {formaterEuros(l.modestes)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-gray-200 bg-gray-50">
                    <th scope="row" className="px-4 py-4 font-medium text-gray-900">
                      Par personne supplémentaire
                    </th>
                    <td className="px-4 py-4 text-gray-800">
                      + {formaterEuros(plafond("tresModestes", 6) - plafond("tresModestes", 5))}
                    </td>
                    <td className="px-4 py-4 text-gray-800">
                      + {formaterEuros(plafond("modestes", 6) - plafond("modestes", 5))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-gray-600 mt-4">
              Barème en vigueur au 1<sup>er</sup> janvier {ANNEE_BAREME}.{" "}
              <a
                href={SOURCE_BAREME.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 underline hover:text-orange-700"
              >
                Source : {SOURCE_BAREME.libelle}
                <ExternalLink size={13} />
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Ce que ça donne comme aide */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Ce que chaque catégorie donne comme aide</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg border-2 border-blue-200">
                <h3 className="text-xl font-bold text-blue-700 mb-3">Ressources très modestes</h3>
                <p className="text-3xl font-bold text-gray-900 mb-2">70 %</p>
                <p className="text-gray-700 leading-relaxed">
                  du montant des travaux, dans la limite d'un plafond de travaux de{" "}
                  {formaterEuros(PLAFOND_TRAVAUX)} HT — soit une aide qui peut atteindre{" "}
                  <strong>{formaterEuros(AIDE_MAX.tresModestes)}</strong>.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border-2 border-yellow-200">
                <h3 className="text-xl font-bold text-yellow-700 mb-3">Ressources modestes</h3>
                <p className="text-3xl font-bold text-gray-900 mb-2">50 %</p>
                <p className="text-gray-700 leading-relaxed">
                  du montant des travaux, dans la même limite de{" "}
                  {formaterEuros(PLAFOND_TRAVAUX)} HT — soit une aide qui peut atteindre{" "}
                  <strong>{formaterEuros(AIDE_MAX.modestes)}</strong>.
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <div className="flex gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={22} />
                <p className="text-gray-800 leading-relaxed">
                  <strong>Ne confondez pas les deux chiffres.</strong> Les{" "}
                  {formaterEuros(PLAFOND_TRAVAUX)} sont un plafond de <em>travaux</em>, pas
                  un montant d'aide : c'est la dépense maximale prise en compte dans le
                  calcul. L'aide, elle, est le pourcentage appliqué à cette dépense, soit{" "}
                  {formaterEuros(AIDE_MAX.tresModestes)} au maximum. C'est la confusion la
                  plus répandue sur le sujet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Les autres conditions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Les ressources ne suffisent pas</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Passer sous les plafonds est nécessaire mais pas suffisant. Trois autres
              conditions s'appliquent, cumulativement.
            </p>
            <div className="space-y-6">
              {[
                {
                  titre: "Une condition d'âge ou de perte d'autonomie",
                  texte:
                    "À partir de 70 ans, aucune justification n'est demandée. Entre 60 et 69 ans, il faut un justificatif de GIR 1 à 6. En situation de handicap, un taux d'incapacité d'au moins 50 %, l'AAH ou la PCH ouvrent le droit sans condition d'âge.",
                },
                {
                  titre: "Être propriétaire occupant",
                  texte:
                    "Le logement doit vous appartenir et constituer votre résidence principale. Les propriétaires bailleurs sont éligibles sous conditions particulières. Les locataires du parc privé passent par leur propriétaire ; ceux du parc social, par leur bailleur.",
                },
                {
                  titre: "Un logement achevé depuis plus de 15 ans",
                  texte:
                    "L'ancienneté du logement est vérifiée à l'instruction. Elle ne pose de difficulté que pour les constructions récentes.",
                },
              ].map((c) => (
                <div key={c.titre} className="border-l-4 border-orange-500 pl-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{c.titre}</h3>
                  <p className="text-gray-700 leading-relaxed">{c.texte}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-10">Questions fréquentes sur les plafonds</h2>
            <div className="space-y-6">
              {FAQ.map((q) => (
                <div key={q.question} className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{q.question}</h3>
                  <p className="text-gray-700 leading-relaxed">{q.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Maillage */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold mb-4">Pour aller plus loin</h2>
            <div className="flex flex-wrap gap-3">
              {[
                ["/maprimeadapt", "Tout sur MaPrimeAdapt'"],
                ["/cumul-aides-ma-prime-adapt-idf", "Cumuler les aides en Île-de-France"],
                ["/blog/reste-a-charge-adaptation-logement", "Calculer son reste à charge"],
                ["/aidants", "Vous cherchez pour un proche"],
              ].map(([to, label]) => (
                <Link
                  key={to}
                  to={to}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:border-orange-500 hover:text-orange-700 transition-colors"
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-orange-600 to-orange-700 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Vérifiez votre catégorie en quatre minutes
          </h2>
          <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
            Le simulateur vous situe par rapport aux plafonds et estime le montant de
            l'aide et votre reste à charge.
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <Link
              to="/simulateur"
              onClick={() => trackSimulatorClick("plafonds_cta")}
              className="inline-flex items-center gap-2 bg-white text-orange-700 text-xl font-bold px-10 py-4 rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              <Calculator size={22} />
              Faire la simulation
            </Link>
            <a
              href="tel:0939035444"
              onClick={() => trackPhoneClick("plafonds_cta")}
              className="bg-transparent border-2 border-white text-white text-xl font-bold px-10 py-4 rounded-lg hover:bg-white hover:text-orange-700 transition-colors flex items-center gap-2"
            >
              <Phone size={24} />
              09 39 03 54 44
            </a>
          </div>
        </div>
      </section>

      <GuidesLies slugs={["reste-a-charge-adaptation-logement", "refus-maprimeadapt-motifs-recours"]} />

      </main>

      <Footer />
    </div>
  );
};

export default PlafondsRessources;
